import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

const STALE_MS = 30_000; // 30s stale time for all caches

interface CacheEntry<T = unknown[]> {
  data: T;
  timestamp: number;
}

interface FilteredCacheEntry<T = unknown[]> extends CacheEntry<T> {
  filters: Record<string, string>;
}

interface IntelDataContextValue {
  // Predictions (filtered cache)
  predictionsCache: FilteredCacheEntry | null;
  cachePredictions: (data: unknown[], filters: Record<string, string>) => void;

  // Influencers (shared across pages)
  influencersCache: CacheEntry | null;
  cacheInfluencers: (data: unknown[]) => void;

  // Claims
  claimsCache: CacheEntry | null;
  cacheClaims: (data: unknown[]) => void;

  // Scoreboard
  scoreboardCache: CacheEntry | null;
  cacheScoreboard: (data: unknown[]) => void;

  // Stats charts
  statsCache: CacheEntry<{ episodes: unknown[]; claims: unknown[]; predictions: unknown[] }> | null;
  cacheStats: (data: { episodes: unknown[]; claims: unknown[]; predictions: unknown[] }) => void;

  // Pipeline stats (CHO-196)
  pipelineStatsCache: CacheEntry<Record<string, number>> | null;
  cachePipelineStats: (data: Record<string, number>) => void;

  // Prediction attrition funnel (CHO-581)
  funnelCache: CacheEntry<{ total: number; as_of: string; stages: unknown[] }> | null;
  cacheFunnel: (data: { total: number; as_of: string; stages: unknown[] }) => void;

  // Staleness check
  isStale: (timestamp: number | undefined) => boolean;
}

const IntelDataContext = createContext<IntelDataContextValue | null>(null);

interface IntelDataProviderProps {
  children: ReactNode;
}

export function IntelDataProvider({ children }: IntelDataProviderProps) {
  const [predictionsCache, setPredictionsCache] = useState<FilteredCacheEntry | null>(null);
  const [influencersCache, setInfluencersCache] = useState<CacheEntry | null>(null);
  const [claimsCache, setClaimsCache] = useState<CacheEntry | null>(null);
  const [scoreboardCache, setScoreboardCache] = useState<CacheEntry | null>(null);
  const [statsCache, setStatsCache] = useState<CacheEntry<{ episodes: unknown[]; claims: unknown[]; predictions: unknown[] }> | null>(null);
  const [pipelineStatsCache, setPipelineStatsCache] = useState<CacheEntry<Record<string, number>> | null>(null);
  const [funnelCache, setFunnelCache] = useState<CacheEntry<{ total: number; as_of: string; stages: unknown[] }> | null>(null);

  const cachePredictions = useCallback((data: unknown[], filters: Record<string, string>) => {
    setPredictionsCache({ data, filters, timestamp: Date.now() });
  }, []);

  const cacheInfluencers = useCallback((data: unknown[]) => {
    setInfluencersCache({ data, timestamp: Date.now() });
  }, []);

  const cacheClaims = useCallback((data: unknown[]) => {
    setClaimsCache({ data, timestamp: Date.now() });
  }, []);

  const cacheScoreboard = useCallback((data: unknown[]) => {
    setScoreboardCache({ data, timestamp: Date.now() });
  }, []);

  const cacheStats = useCallback((data: { episodes: unknown[]; claims: unknown[]; predictions: unknown[] }) => {
    setStatsCache({ data, timestamp: Date.now() });
  }, []);

  const cachePipelineStats = useCallback((data: Record<string, number>) => {
    setPipelineStatsCache({ data, timestamp: Date.now() });
  }, []);

  const cacheFunnel = useCallback((data: { total: number; as_of: string; stages: unknown[] }) => {
    setFunnelCache({ data, timestamp: Date.now() });
  }, []);

  const isStale = useCallback((timestamp: number | undefined) => {
    if (!timestamp) return true;
    return Date.now() - timestamp > STALE_MS;
  }, []);

  return (
    <IntelDataContext.Provider value={{
      predictionsCache, cachePredictions,
      influencersCache, cacheInfluencers,
      claimsCache, cacheClaims,
      scoreboardCache, cacheScoreboard,
      statsCache, cacheStats,
      pipelineStatsCache, cachePipelineStats,
      funnelCache, cacheFunnel,
      isStale,
    }}>
      {children}
    </IntelDataContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useIntelData(): IntelDataContextValue {
  const ctx = useContext(IntelDataContext);
  if (!ctx) throw new Error('useIntelData must be used within IntelDataProvider');
  return ctx;
}
